import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Save } from 'lucide-react'
import { createUser, getLocations, getUser, uploadUserPhoto } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { useToast } from '../../components/Toast.jsx'
import { useSession } from '../../session'
import { Field, ROLES, photoSrc, roleList, userActive, validEmail } from './masterShared'
import { CreateOption } from '../../components/CreateOption'

const BLANK = {
  username: '', full_name: '', email: '', password: '', roles: ['sales'], is_active: true, location_id: '',
  phone: '', address: '', date_employed: '', national_id: '', photo_url: '',
}

// Add or change a user (admins only): sign-in details, what they can open,
// their home location and staff details.
export default function UserForm() {
  const { userId } = useParams()
  const isNew = !userId
  const nav = useNavigate()
  const qc = useQueryClient()
  const { notify } = useToast()
  const { user: me } = useSession()
  const isMe = !isNew && String(me?.user_id) === String(userId)
  const { data: locations = [] } = useQuery({ queryKey: ['locations'], queryFn: getLocations })
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState({})
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(!isNew)
  const fileInput = useRef(null)

  useEffect(() => {
    if (isNew) { setForm(BLANK); return }
    setLoading(true)
    getUser(userId)
      .then((u) => setForm({
        username: u.username || '', full_name: u.full_name || '', email: u.email || '', password: '',
        roles: roleList(u.role), is_active: userActive(u), location_id: u.location_id ? String(u.location_id) : '',
        phone: u.phone || '', address: u.address || '', date_employed: String(u.date_employed || '').slice(0, 10),
        national_id: u.national_id || '', photo_url: u.photo_url || '',
      }))
      .catch((e) => setError(e?.response?.status === 404 ? 'This user wasn’t found.' : (e.message || 'Couldn’t load the user.')))
      .finally(() => setLoading(false))
  }, [userId, isNew])

  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const isAdmin = form.roles.includes('admin')
  const toggleRole = (value, on) => set({ roles: on ? [...form.roles, value] : form.roles.filter((r) => r !== value) })

  const pickPhoto = async (file) => {
    if (!file) return
    setUploading(true)
    setError('')
    try {
      const res = await uploadUserPhoto(file, isNew ? undefined : userId)
      set({ photo_url: res.photo_url || '' })
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Couldn’t upload the photo.')
    } finally {
      setUploading(false)
    }
  }

  const save = async () => {
    const e = {}
    if (!form.username.trim()) e.username = 'Give them a username to sign in with.'
    if (!validEmail(form.email)) e.email = 'That email doesn’t look right.'
    if (isNew && form.password.length < 8) e.password = 'At least 8 characters.'
    if (!isNew && form.password && form.password.length < 8) e.password = 'At least 8 characters, or leave it blank.'
    if (!form.roles.length) e.roles = 'Tick at least one.'
    if (!isAdmin && !form.location_id) e.location_id = 'Everyone except admins works at one location.'
    setErrors(e)
    if (Object.keys(e).length) { setError('Check the highlighted fields.'); return }
    setSaving(true)
    setError('')
    try {
      await createUser({
        user_id: isNew ? undefined : Number(userId),
        username: form.username.trim(), full_name: form.full_name.trim(), email: form.email.trim(),
        password: form.password, role: ROLES.map((r) => r.value).filter((r) => form.roles.includes(r)).join(','),
        is_active: form.is_active ? '1' : '0', location_id: form.location_id ? Number(form.location_id) : null,
        phone: form.phone.trim(), address: form.address.trim(), date_employed: form.date_employed || '',
        national_id: form.national_id.trim(), photo_url: form.photo_url,
      })
      await qc.invalidateQueries({ queryKey: ['users'] })
      notify(isNew ? `${form.full_name.trim() || form.username.trim()} can now sign in` : 'User saved')
      nav('/master/users')
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Couldn’t save the user.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="ci-form-page"><Spinner label="Loading user…" /></div>

  const pic = photoSrc(form.photo_url)
  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/master/users" label="Users" />}
        title={isNew ? 'New user' : (form.full_name || form.username || 'User')}
        badge={!isNew && (form.is_active ? <StatusBadge label="Active" tone="good" /> : <StatusBadge label="Inactive" tone="muted" />)}
        meta={isNew ? 'Someone who signs in to ASYS Retail.' : `@${form.username}`}
      />
      {error && <ErrorBanner message={error} />}

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Staff details</div>
        <div className="mf-person">
          <div className="mf-photo">
            {pic ? <img src={pic} alt="" /> : <span>{(form.full_name || form.username || '?').slice(0, 1).toUpperCase()}</span>}
            <input ref={fileInput} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => pickPhoto(e.target.files?.[0])} />
            <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" disabled={uploading} onClick={() => fileInput.current?.click()}>
              <Camera size={13} />{uploading ? 'Uploading…' : (pic ? 'Change photo' : 'Add photo')}
            </button>
          </div>
          <div className="mf-person-fields">
            <div className="ci-form-meta-top two">
              <Field label="Full name"><input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} /></Field>
              <Field label="Phone (optional)"><input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
            </div>
            <div className="ci-form-meta-top three">
              <Field label="Address (optional)"><input value={form.address} onChange={(e) => set({ address: e.target.value })} /></Field>
              <Field label="Date employed (optional)"><input type="date" value={form.date_employed} onChange={(e) => set({ date_employed: e.target.value })} /></Field>
              <Field label="National ID (optional)"><input value={form.national_id} onChange={(e) => set({ national_id: e.target.value })} placeholder="e.g. NIN" /></Field>
            </div>
          </div>
        </div>
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Sign-in</div>
        <div className="ci-form-meta-top three">
          <Field label="Username" error={errors.username} hint="They sign in with this.">
            <input value={form.username} onChange={(e) => set({ username: e.target.value })} autoComplete="off" />
          </Field>
          <Field label={isNew ? 'Password' : 'New password (optional)'} error={errors.password} hint={isNew ? 'At least 8 characters. Share it with them privately.' : 'Leave blank to keep their current password.'}>
            <input type="password" value={form.password} onChange={(e) => set({ password: e.target.value })} autoComplete="new-password" />
          </Field>
          <Field label="Email (optional)" error={errors.email} hint="Used to reset a forgotten password.">
            <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
          </Field>
        </div>
        <div className="mf-checks">
          <label className="ci-form-toggle mf-check">
            <input type="checkbox" checked={form.is_active} disabled={isMe} onChange={(e) => set({ is_active: e.target.checked })} />
            Can sign in
          </label>
          {isMe && <span className="mf-hint">You can’t turn off your own sign-in.</span>}
        </div>
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">What they can open</div>
        {errors.roles && <span className="acc-field-error">{errors.roles}</span>}
        <div className="mf-role-grid">
          {ROLES.map((r) => {
            const locked = isMe && r.value === 'admin'
            return (
              <label key={r.value} className={`mf-role-option${form.roles.includes(r.value) ? ' on' : ''}`}>
                <input type="checkbox" checked={form.roles.includes(r.value)} disabled={locked} onChange={(e) => toggleRole(r.value, e.target.checked)} />
                <span><strong>{r.label}</strong><span>{locked ? 'You can’t remove your own admin access.' : r.desc}</span></span>
              </label>
            )
          })}
        </div>
        <div className="ci-form-meta-top two">
          <Field label="Home location" error={errors.location_id} hint={isAdmin ? 'Admins work across all locations.' : 'Where they sell and count stock.'}>
            <select value={form.location_id} onChange={(e) => set({ location_id: e.target.value })}>
              <option value="">{isAdmin ? 'All locations' : 'Choose a location…'}</option>
              <CreateOption kind="location" />
              {locations.map((l) => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving || uploading} onClick={save}><Save size={14} />{saving ? 'Saving…' : (isNew ? 'Add user' : 'Save')}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/master/users')}>Cancel</button>
        </div>
      </div>
    </div>
  )
}
